import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Wand2, ChevronDown, ChevronUp, Sliders, AlertCircle, Loader2, Search, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AudioPlayer from '../AudioPlayer';
import { toast } from 'sonner';

const VOICE_STYLES = [
  { id: 'default', label: 'Default', desc: 'Standard natural voice' },
  { id: 'conversational', label: 'Conversational', desc: 'Casual and friendly tone' },
  { id: 'narrative', label: 'Narrative', desc: 'Storytelling and narration' },
  { id: 'newscast', label: 'Newscast', desc: 'Professional news anchor' },
  { id: 'calm', label: 'Calm', desc: 'Soothing and relaxed' },
  { id: 'cheerful', label: 'Cheerful', desc: 'Upbeat and positive' },
  { id: 'sad', label: 'Sad', desc: 'Emotional and somber' },
  { id: 'whisper', label: 'Whisper', desc: 'Soft whispering voice' },
  { id: 'multilingual', label: 'Multilingual', desc: 'Cross-language support' },
];

const TTSStudio = () => {
  const { token, API, refreshUser } = useAuth();
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('cherry_en');
  const [speed, setSpeed] = useState(1.0);
  const [volume, setVolume] = useState(50);
  const [style, setStyle] = useState('default');
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [voices, setVoices] = useState([]);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [langFilter, setLangFilter] = useState('English');

  useEffect(() => {
    axios.get(`${API}/voices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVoices(r.data.voices))
      .catch(() => {});
  }, [token, API]);

  const languages = useMemo(() => {
    return [...new Set(voices.map(v => v.language))].sort();
  }, [voices]);

  const filteredVoices = useMemo(() => {
    return voices.filter(v => {
      const matchLang = !langFilter || v.language === langFilter;
      const matchSearch = !voiceSearch || v.name.toLowerCase().includes(voiceSearch.toLowerCase());
      return matchLang && matchSearch;
    });
  }, [voices, langFilter, voiceSearch]);

  const selectedVoice = voices.find(v => v.id === voice);
  const charCount = text.length;

  const handleGenerate = async () => {
    if (!text.trim()) { setError('Please enter text to synthesize.'); return; }
    setError('');
    setLoading(true);
    setAudioUrl('');
    try {
      const res = await axios.post(`${API}/tts/synthesize`,
        { text, voice, speed, pitch: 1.0, volume, style },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAudioUrl(res.data.audio_url);
      const mergeMsg = res.data.merged ? ' (merged from multiple parts)' : '';
      toast.success(`Audio generated!${mergeMsg} ${res.data.chars_used} chars used.`);
      refreshUser?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to generate audio. Check your API key.';
      setError(msg);
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="glass-card rounded-2xl p-6 border border-border">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold font-outfit text-foreground text-lg">Text to Speech Studio</h2>
            <p className="text-xs text-muted-foreground">
              {voices.length > 0 ? `${voices.length} voices · ${languages.length} languages` : 'Convert text to natural AI voice'}
            </p>
          </div>
        </div>

        {/* Text Input - No limit */}
        <div className="relative">
          <textarea
            data-testid="tts-text-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Enter the text you want to convert to speech... No character limit — write as much as you need."
            rows={6}
            className="w-full px-4 py-4 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all text-sm resize-vertical"
          />
          <div className="absolute bottom-3 right-3">
            <span data-testid="char-count" className={`text-xs px-2 py-0.5 rounded-full ${
              charCount > 5000 ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground bg-secondary'
            }`}>
              {charCount.toLocaleString()} characters
            </span>
          </div>
        </div>

        {/* Voice Style Selector */}
        <div className="mt-4 space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Sparkles className="w-4 h-4 text-primary" />
            Voice Style
          </label>
          <div className="flex flex-wrap gap-2">
            {VOICE_STYLES.map(s => (
              <button
                key={s.id}
                data-testid={`style-${s.id}`}
                onClick={() => setStyle(s.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  style === s.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80'
                }`}
                title={s.desc}
              >
                {s.label}
              </button>
            ))}
          </div>
          {style !== 'default' && (
            <p className="text-xs text-muted-foreground">
              {VOICE_STYLES.find(s => s.id === style)?.desc}
            </p>
          )}
        </div>

        {/* Voice Selector */}
        <div className="mt-4 space-y-2">
          <label className="block text-sm font-medium text-foreground">Voice</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search voices..."
                value={voiceSearch}
                onChange={e => setVoiceSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2.5 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground text-xs focus:border-primary"
              />
            </div>
            <div className="relative">
              <select
                data-testid="lang-select"
                value={langFilter}
                onChange={e => setLangFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2.5 rounded-xl bg-background border border-border text-foreground text-xs focus:border-primary cursor-pointer"
              >
                <option value="">All Languages</option>
                {languages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div className="relative">
            <select
              data-testid="voice-select"
              value={voice}
              onChange={e => setVoice(e.target.value)}
              className="w-full appearance-none px-4 py-3 rounded-xl bg-background border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary text-sm cursor-pointer"
            >
              {filteredVoices.length === 0 && <option value="">No voices found</option>}
              {filteredVoices.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name} — {v.gender === 'female' ? 'Female' : 'Male'} · {v.accent}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
          {selectedVoice && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600" />
              {selectedVoice.name} · {selectedVoice.gender === 'female' ? 'Female' : 'Male'} · {selectedVoice.language} · {selectedVoice.accent}
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">{selectedVoice.category}</span>
            </div>
          )}
        </div>

        {/* Advanced Controls - Shown by default, collapsible */}
        <button
          data-testid="advanced-toggle"
          onClick={() => setShowAdvanced(p => !p)}
          className="mt-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Sliders className="w-4 h-4" />
          Advanced Controls
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showAdvanced && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5 p-4 rounded-xl bg-secondary/50">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Speed</label>
                <span className="text-sm text-primary font-mono">{speed.toFixed(1)}x</span>
              </div>
              <input data-testid="speed-slider" type="range" min="0.5" max="2.0" step="0.1" value={speed}
                onChange={e => setSpeed(parseFloat(e.target.value))} className="w-full" />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0.5x Slow</span><span>2.0x Fast</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Volume</label>
                <span className="text-sm text-primary font-mono">{volume}</span>
              </div>
              <input data-testid="volume-slider" type="range" min="0" max="100" step="5" value={volume}
                onChange={e => setVolume(parseInt(e.target.value))} className="w-full" />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div data-testid="tts-error" className="mt-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/20">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Generate */}
        <button
          data-testid="generate-btn"
          onClick={handleGenerate}
          disabled={loading || !text.trim()}
          className="mt-5 w-full gradient-btn py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating... Please wait
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              Generate Voice
            </>
          )}
        </button>
      </div>

      {/* Audio Player */}
      {audioUrl && (
        <div className="anim-scalein">
          <AudioPlayer
            audioUrl={audioUrl}
            audioUrls={[audioUrl]}
            voiceName={selectedVoice ? `${selectedVoice.name} — ${selectedVoice.language}` : voice}
            textPreview={text}
          />
        </div>
      )}
    </div>
  );
};

export default TTSStudio;
